import type {CSSProperties} from 'react';
import {WEDGE_BRIEF_FOOTER as F} from '@territory/tokens';
import {wedgeExactRole as r} from './wedgeRoles.js';

export type WedgeBriefFooterLook = 'brieffoot-req' | 'brieffoot-outcome' | 'brieffoot-resume' | 'brieffoot-trial-cta';

export const isWedgeBriefFooterLook = (look: string | undefined): look is WedgeBriefFooterLook => look !== undefined && look.startsWith('brieffoot-');

/* Released controls of the foot of a brief. The REQ chip is a bare mono 9px outlined chip at 70 percent opacity. An outcome tile of the
   post meeting prompt is a flex 1 bare tile 44px tall at least, with the label capitalised. The resume pill floats 150px above the
   bottom edge of the page, centred, a round scrim pill with a hairline edge, a blur behind it and the fixed drop shadow. The trial
   CTA is the full width accent bar of the plan card, 42px tall. Each keeps the declaration order of the source so the shipped ink rules
   keep matching the serialized text. None draws a transparent target or an anchor (`drawsHit: false` in the registry): the chip is the
   whole box, the tiles touch their row and the pill and the bar are the only control of their line. */
export function wedgeBriefFooterStyle(look: WedgeBriefFooterLook): CSSProperties {
  switch (look) {
    case 'brieffoot-req': return {
      background: 'transparent', border: `1px solid ${r('cardBorder')}`, borderRadius: 6, color: r('labelQuad'), fontFamily: r('mono'), fontSize: 9,
      fontWeight: 600, letterSpacing: 0.06, textTransform: 'uppercase', padding: '4px 8px', cursor: 'pointer', opacity: 0.7,
      WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
    };
    case 'brieffoot-outcome': return {
      flex: 1, padding: '9px 0', background: r('bgElev'), border: `1px solid ${r('cardBorder')}`, borderRadius: 8, color: r('label'), fontSize: 13,
      fontWeight: 600, fontFamily: r('font'), cursor: 'pointer', textTransform: 'capitalize', minHeight: 44, WebkitTapHighlightColor: 'transparent',
      touchAction: 'manipulation',
    };
    case 'brieffoot-resume': return {
      position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: 150, zIndex: 6, display: 'inline-flex', alignItems: 'center', gap: 7,
      padding: '9px 16px', borderRadius: 999, background: r('scrim'), border: `0.5px solid ${r('separatorBold')}`, backdropFilter: 'blur(14px)',
      WebkitBackdropFilter: 'blur(14px)', color: r('accent'), cursor: 'pointer', fontFamily: r('font'), fontSize: 12.5, fontWeight: 800,
      boxShadow: F.resumeShadow, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
    };
    case 'brieffoot-trial-cta': return {
      marginTop: 12, width: '100%', height: 42, borderRadius: 12, background: r('accent'), color: r('onAccent'), border: 0, cursor: 'pointer', fontSize: 13.5,
      fontWeight: 800, fontFamily: r('font'),
    };
  }
}
